import { format } from 'date-fns';
import { Avatar, Box, Link, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Appointment,
  LoadedUser,
  ModalData,
  PiercingLocation,
  Section,
  SectionRow,
} from '../../interfaces';
import {
  convertMinsToHrsMins,
  formatDollarAmount,
  getBooleanText,
  getEmployeeNameFromAppointment,
  getFullTattooLocation,
  getInitials,
  isNil,
} from '../global/helper';
import WaiverIcon from '../../components/icons/WaiverIcon';
import { isCardPaymentMethod } from '../../helpers/payments';
import Badge from '../../components/global/Badge';
import { generateColorForArtist } from '../../services/business';
import {
  createReferralSourceSection,
  getAllRefImages,
  getCustomerDetails,
  getSelectedArtistsText,
  isAppointmentDepositPaid,
} from '../../helpers/appointments';
import { getBusinessCurrency } from '../../helpers/businesses';
import { getUserNameFromBusiness } from '../../helpers/global';
import { getWaiverRequestStatusColor } from '../../helpers/waivers';
import { GetWaiversData } from '../../types/waivers';
import {
  getCustomerRelationDetailRows,
  getPrimaryCustomerDetails,
} from './customerModal';
import { getAppointmentFlashDesignUrl } from '../../helpers/flashbooks';
import CustomerNameAge from '../../components/appointments/CustomerNameAge';
import { countActivityEvents } from '../../components/modal/ActivityHistorySection';
import {
  ServiceDetailGridField,
  ServiceDetailNoteField,
  ServiceDetailPhotoRow,
} from '../../components/modal/ServiceDetailBody';

const SERVICE_CATEGORY_LABELS: Record<string, string> = {
  TATTOO: 'Tattoo',
  PIERCING: 'Piercing',
  PERMANENT_MAKEUP: 'Permanent Makeup',
  SPA: 'Spa',
};

// Unanswered (null) stays hidden; an explicit "No" still shows.
const answerText = (value: boolean | null | undefined) =>
  isNil(value) ? undefined : getBooleanText(value ?? undefined);

const TYPE_PILL_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 22,
  padding: '0 10px',
  borderRadius: 50,
  border: '1px solid var(--sidebar-border-light)',
  fontSize: 'var(--sidebar-font-xs)',
  fontWeight: 700,
  color: 'var(--sidebar-body)',
  whiteSpace: 'nowrap',
};

export function createServiceDetailsSection({
  appointment,
  service,
  piercingLocation,
}: {
  appointment: Pick<
    Appointment,
    | 'internalNotes'
    | 'tattooDetails'
    | 'availability'
    | 'otherNotes'
    | 'piercingLocationId'
  >;
  service:
    | { serviceName?: string | null; serviceCategory?: string | null }
    | null
    | undefined;
  piercingLocation:
    | { locationCategory?: string | null; locationType?: string | null }
    | null
    | undefined;
}): Section {
  return {
    name: 'Service Details',
    rows: [
      {
        type: 'TEXT',
        label: 'Internal Notes',
        value: [appointment.internalNotes || ''],
      },
      {
        type: 'TEXT',
        label: 'Service',
        value: [service?.serviceName || ''],
      },
      service?.serviceCategory === 'PIERCING' && {
        type: 'TEXT',
        label: 'Piercing Location',
        value: [piercingLocation?.locationCategory || ''],
      },
      service?.serviceCategory === 'PIERCING' && {
        type: 'TEXT',
        label: 'Location Details',
        value: [piercingLocation?.locationType || ''],
      },
      {
        type: 'TEXT',
        label: 'Details',
        value: [appointment.tattooDetails || ''],
      },
      !isNil(appointment.availability) && {
        type: 'TEXT',
        label: 'General Availability',
        value: [appointment.availability || ''],
      },
      !isNil(appointment.otherNotes) && {
        type: 'TEXT',
        label: 'Other Notes',
        value: [appointment.otherNotes || ''],
      },
    ].filter(Boolean) as SectionRow[],
  };
}

export function getClientSection(
  appointment: Appointment,
  button?: {
    text: string;
    action: (customerId: string) => void;
  },
): Section {
  const customerDetails = getCustomerDetails(appointment);

  const isTattoo = appointment.serviceCategory === 'TATTOO';
  const firstTattoo = isTattoo ? getBooleanText(appointment.isFirstTattoo) : '';
  const newCustomer = isTattoo ? getBooleanText(appointment.isNewCustomer) : '';

  const client: Section = {
    name: 'Client',
    rows: [
      {
        type: 'NODE',
        label: 'Name',
        value: [
          <CustomerNameAge
            appointment={appointment}
            typographyVariant="body2"
            minorForAppointment
          />,
        ],
      },
      {
        type: 'TEXT',
        label: 'Booking Location',
        value: [appointment.tripCity || ''],
      },
      {
        type: 'TEXT',
        label: 'Email',
        value: [customerDetails.email || ''],
      },
      {
        type: 'PHONE',
        label: 'Phone',
        value: [customerDetails.phoneNumber || ''],
      },
      {
        type: 'TEXT',
        label: 'Birthday',
        value: [
          customerDetails.dob
            ? format(new Date(`${customerDetails.dob}T00:00:00`), 'MM/dd/yyyy')
            : '',
        ],
      },
      {
        type: 'TEXT',
        label: 'IG Handle',
        value: [
          !appointment.customerGuest ? appointment.customer.igHandle || '' : '',
        ],
      },
      {
        type: 'TEXT',
        label: 'Skin Tone',
        value: [appointment.skinTone || ''],
      },
      {
        type: 'TEXT',
        label: 'First Tattoo',
        value: [firstTattoo || ''],
      },
      {
        type: 'TEXT',
        label: 'New Customer',
        value: [newCustomer || ''],
      },
      {
        type: 'TEXT',
        label: 'Internal Notes',
        value: [appointment.customer.internalNotes || ''],
      },
      ...getPrimaryCustomerDetails(customerDetails.primaryCustomer),
      ...getCustomerRelationDetailRows(appointment.customer),
    ],
    buttonText: button?.text,
    buttonAction: button?.action
      ? () => button.action(appointment.customerId)
      : undefined,
  };
  return client;
}

// Set modal data based on the appointment data
export function createModalData({
  appointment,
  openCheckoutModal,
  sizeOptions,
  navigateToCustomer,
  loadedUser,
  piercingLocations,
  refreshModal,
  waiverButtonAction,
  waivers,
  getAllInDepositAmount,
  redesignActive,
  onOpenChat,
  showChat,
  hasTransactions,
}: {
  appointment: Appointment;
  openCheckoutModal: () => void;
  sizeOptions: { name: string | null; value: string | null }[] | undefined;
  navigateToCustomer: (customerId: string) => void;
  loadedUser: LoadedUser | undefined;
  piercingLocations: PiercingLocation[] | null;
  refreshModal: (appointmentId: string) => void;
  redesignActive?: boolean;
  onOpenChat?: () => void;
  showChat?: boolean;
  hasTransactions?: boolean;
  waiverButtonAction?: (appointment: Appointment) => void;
  waivers: GetWaiversData[] | null | undefined;
  getAllInDepositAmount?: (appointment: Appointment) => number;
}): ModalData {
  const dateTimeFormat = "MMM d, yyyy 'at' h:mmaaa";
  const isTattoo = appointment.serviceCategory === 'TATTOO';

  const service = loadedUser?.business.services?.find(
    tempService => tempService.serviceId === appointment.serviceId,
  );
  const piercingLocation = piercingLocations?.find(
    location => location.locationId === appointment.piercingLocationId,
  );

  const isConfirmedOrScheduled =
    appointment.status === 'CONFIRMED' || appointment.status === 'SCHEDULED';

  const isDepositPaid = isAppointmentDepositPaid(appointment);

  const status = [];
  if (isConfirmedOrScheduled && appointment.checkoutSessionActive) {
    status.push('Checkout link sent');
  } else if (
    isConfirmedOrScheduled &&
    appointment.appointmentType === 'CONSULTATION'
  ) {
    status.push('Consultation scheduled');
  } else if (isConfirmedOrScheduled && appointment.checkoutSessionCompleted) {
    status.push('Appointment completed');
  } else if (isConfirmedOrScheduled && appointment.wasNoShow) {
    status.push('No show');
  } else if (appointment.status === 'CONFIRMED') {
    status.push('Appointment confirmed');
  } else if (
    appointment.status === 'SCHEDULED' &&
    appointment.depositPrice &&
    !isDepositPaid
  ) {
    status.push('Appointment scheduled (deposit unpaid)');
  } else if (appointment.status === 'SCHEDULED') {
    status.push('Appointment scheduled');
  } else if (appointment.status === 'CANCELED') {
    status.push('Appointment canceled');
  } else if (appointment.status === 'QUEUED') {
    status.push('Appointment in walk-in queue');
    if (appointment.joinedQueueAt) {
      status.push(
        `Joined queue on ${format(
          new Date(appointment.joinedQueueAt),
          dateTimeFormat,
        )}`,
      );
    }
  } else if (appointment.status === 'ARCHIVED') {
    status.push(
      `Appointment archived${
        appointment.archivedAt
          ? ` on ${format(new Date(appointment.archivedAt), 'MM/dd/yyyy')}`
          : ''
      }`,
    );
  } else {
    status.push(appointment.status);
  }

  if (appointment.startDateTime && appointment.endDateTime) {
    const startDate = new Date(appointment.startDateTime!);
    const endDate = new Date(appointment.endDateTime!);

    status.push(format(startDate, 'eeee MMMM d, yyyy'));
    status.push(`${format(startDate, 'p')} - ${format(endDate, 'p')}`);
  }

  const canceledAt =
    appointment.canceledOn && appointment.status === 'CANCELED'
      ? format(new Date(appointment.canceledOn), dateTimeFormat)
      : '';

  const employeeName = getEmployeeNameFromAppointment(appointment);

  let size = appointment.tattooSize;
  const sizeName = sizeOptions?.find(option => option.value === size);
  if (sizeName) {
    size = sizeName.name ?? undefined;
  }

  const bookedByCustomer = appointment.bookerUserId === appointment.customerId;
  const bookerName = getUserNameFromBusiness(
    loadedUser,
    bookedByCustomer,
    appointment.bookerUserId,
    true,
  );

  const canceledByCustomer =
    appointment.canceledByUserId === appointment.customerId;
  const canceledByName = getUserNameFromBusiness(
    loadedUser,
    canceledByCustomer,
    appointment.canceledByUserId,
  );

  const modifiedByCustomer = appointment.modifiedBy === appointment.customerId;
  const modifiedByName = getUserNameFromBusiness(
    loadedUser,
    modifiedByCustomer,
    appointment.modifiedBy,
    true,
  );

  const overviewWaiverButton =
    appointment.waiverRequest && waiverButtonAction
      ? {
          buttonText: (
            <WaiverIcon
              fill={getWaiverRequestStatusColor({
                appointment,
                waiverRequest: appointment.waiverRequest,
                waivers,
              })}
            />
          ),
          buttonAction: () => waiverButtonAction(appointment),
        }
      : {};

  const isVirtual =
    appointment.isVirtual && appointment.appointmentType === 'CONSULTATION';
  const customerDetails = getCustomerDetails(appointment);
  const scheduledOnText = appointment.scheduledOn
    ? format(new Date(appointment.scheduledOn), dateTimeFormat)
    : '';
  const scheduledText =
    bookerName && appointment.startDateTime
      ? `By ${bookerName}${scheduledOnText ? ` on ${scheduledOnText}` : ''}`
      : '';
  const modifiedText = appointment.modifiedBy
    ? `By ${modifiedByName}${
        appointment.updatedAt
          ? ` on ${format(new Date(appointment.updatedAt), dateTimeFormat)}`
          : ''
      }`
    : '';

  const overview: Section = {
    name: 'Overview',
    rows: [
      {
        type: 'TEXT',
        label: 'Client',
        value: [
          <CustomerNameAge
            appointment={appointment}
            typographyVariant="body2"
            minorForAppointment
          />,
        ],
      },
      {
        type: 'TEXT',
        label: 'Status',
        value: status,
      },
      appointment.status === 'QUEUED' &&
        appointment?.selectedArtists?.length && {
          type: 'TEXT',
          label: 'Preferred Artists',
          value: [
            getSelectedArtistsText(loadedUser, appointment.selectedArtists),
          ],
        },
      {
        type: 'TEXT',
        label: isTattoo ? 'Tattoo Artist' : 'Service Provider',
        value: [employeeName],
      },
      !isTattoo && {
        type: 'TEXT',
        label: 'Service',
        value: [service?.serviceName || ''],
      },
      {
        type: 'TEXT',
        label: 'Scheduled',
        value: [scheduledText],
      },
      !!canceledByName && {
        type: 'TEXT',
        label: 'Canceled by',
        value: [canceledByName],
      },
      !!canceledAt && {
        type: 'TEXT',
        label: 'Canceled on',
        value: [canceledAt],
      },
      !!modifiedText && {
        type: 'TEXT',
        label: 'Modified',
        value: [modifiedText],
      },
      isVirtual &&
        appointment.virtualMeetingProvider === 'PHONE' && {
          type: 'TEXT',
          label: 'Location',
          value: ['Virtual Consultation (Phone Call)'],
        },
      isVirtual &&
        appointment.virtualMeetingProvider === 'PHONE' &&
        customerDetails.phoneNumber && {
          type: 'TEXT',
          label: 'Customer Phone Number',
          value: [customerDetails.phoneNumber],
        },
      isVirtual &&
        appointment.virtualMeetingProvider !== 'PHONE' && {
          type: 'LINK',
          label: 'Virtual Meeting URL',
          value: [
            {
              label: appointment.virtualMeetingUrl || '',
              url: appointment.virtualMeetingUrl || '',
            },
          ],
        },
      (appointment.depositPrice ?? 0) > 0 &&
        !(appointment.checkoutCompletedOn && !isDepositPaid) && {
          type: 'NODE',
          label: 'Deposit',
          value: [
            <Box
              key="deposit"
              display="flex"
              alignItems="center"
              gap={1}
              mt={0.5}>
              <Typography variant="body2">
                <Typography component="span" variant="inherit" fontWeight={700}>
                  {appointment.depositPrice
                    ? formatDollarAmount(
                        getAllInDepositAmount?.(appointment) ??
                          appointment.depositPrice,
                      )
                    : ''}
                </Typography>
                {loadedUser?.business
                  ? ` ${getBusinessCurrency(loadedUser?.business)}`
                  : ''}
              </Typography>
              <Badge
                colorScheme={isDepositPaid ? 'secondary' : 'primary'}
                text={isDepositPaid ? 'Paid' : 'Unpaid'}
                icon={isDepositPaid ? CheckIcon : CloseIcon}
                sx={{
                  width: 'fit-content',
                }}
                tooltip={{
                  placement: 'top',
                  offset: [0, -8],
                  text: isDepositPaid
                    ? `Deposit paid on ${
                        appointment.depositPaidOn || appointment.confirmedOn
                          ? format(
                              new Date(
                                (appointment.depositPaidOn ||
                                  appointment.confirmedOn)!,
                              ),
                              'MM/dd/yyyy',
                            )
                          : 'Unknown'
                      }`
                    : 'Deposit unpaid',
                }}
              />
            </Box>,
          ],
        },
    ].filter(Boolean) as SectionRow[],
    ...overviewWaiverButton,
  };

  const isConsultation = appointment.appointmentType === 'CONSULTATION';
  const categoryLabel =
    SERVICE_CATEGORY_LABELS[appointment.serviceCategory ?? ''];
  const kindLabel = isConsultation ? 'Consultation' : 'Appointment';
  const typeQualifier = isVirtual
    ? appointment.virtualMeetingProvider === 'PHONE'
      ? 'Phone'
      : 'Virtual'
    : appointment.appointmentType === 'WALK_IN'
    ? 'Walk-in'
    : null;
  const appointmentTypeText = [
    categoryLabel ? `${categoryLabel} ${kindLabel.toLowerCase()}` : kindLabel,
    typeQualifier,
  ]
    .filter(Boolean)
    .join(' · ');

  const redesignStartDate = appointment.startDateTime
    ? new Date(appointment.startDateTime)
    : null;
  const redesignEndDate = appointment.endDateTime
    ? new Date(appointment.endDateTime)
    : null;

  const artistAvatarColor =
    appointment.employeeId && loadedUser
      ? generateColorForArtist(appointment.employeeId, loadedUser)
      : null;
  const artistRoleLabel = isTattoo ? 'Tattoo artist' : 'Service provider';

  const overviewSection: Section = {
    name: 'Overview',
    hideHeader: true,
    rows: [
      {
        type: 'NODE',
        label: '',
        value: [
          <Box
            key="overview-when"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '6px',
            }}>
            {redesignStartDate && (
              <Typography
                sx={{
                  fontSize: 'var(--sidebar-font-lg)',
                  fontWeight: 700,
                  color: 'var(--sidebar-ink)',
                }}>
                {format(redesignStartDate, 'eeee, MMMM d, yyyy')}
              </Typography>
            )}
            {redesignStartDate && redesignEndDate && (
              <Typography
                sx={{
                  fontSize: 'var(--sidebar-font-base)',
                  fontWeight: 500,
                  color: 'var(--sidebar-secondary)',
                }}>
                {format(redesignStartDate, 'p')} –{' '}
                {format(redesignEndDate, 'p')}
                {appointment.appointmentLengthInMinutes
                  ? ` · ${convertMinsToHrsMins(
                      appointment.appointmentLengthInMinutes,
                      true,
                    )}`
                  : ''}
              </Typography>
            )}
            {isVirtual && appointment.virtualMeetingProvider === 'PHONE' && (
              <Typography
                sx={{
                  fontSize: 'var(--sidebar-font-base)',
                  fontWeight: 500,
                  color: 'var(--sidebar-secondary)',
                }}>
                Phone call
                {customerDetails.phoneNumber
                  ? ` · ${customerDetails.phoneNumber}`
                  : ''}
              </Typography>
            )}
            {isVirtual &&
              appointment.virtualMeetingProvider !== 'PHONE' &&
              !!appointment.virtualMeetingUrl && (
                <Link
                  href={appointment.virtualMeetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 'var(--sidebar-font-base)',
                    fontWeight: 500,
                    color: 'var(--sidebar-brand)',
                    wordBreak: 'break-all',
                  }}>
                  {appointment.virtualMeetingUrl}
                </Link>
              )}
            <Box component="span" sx={{ ...TYPE_PILL_SX, marginTop: '4px' }}>
              {appointmentTypeText}
            </Box>
          </Box>,
        ],
      },
      appointment.employee
        ? {
            type: 'NODE',
            label: '',
            value: [
              <Box
                key="overview-artist"
                sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 'var(--sidebar-font-2xs)',
                    fontWeight: 700,
                    bgcolor: artistAvatarColor ?? 'var(--sidebar-body)',
                    color: 'var(--sidebar-surface)',
                    flexShrink: 0,
                  }}>
                  {getInitials(appointment.employee)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 'var(--sidebar-font-base)',
                      fontWeight: 700,
                      color: 'var(--sidebar-ink)',
                    }}>
                    {employeeName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 'var(--sidebar-font-xs)',
                      color: 'var(--sidebar-tertiary)',
                    }}>
                    {artistRoleLabel}
                  </Typography>
                </Box>
              </Box>,
            ],
          }
        : appointment.status === 'QUEUED' && {
            type: 'NODE',
            label: '',
            value: [
              <Box
                key="overview-artist-unassigned"
                sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography
                  sx={{
                    fontSize: 'var(--sidebar-font-base)',
                    fontWeight: 700,
                    color: 'var(--sidebar-ink)',
                  }}>
                  Unassigned
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'var(--sidebar-font-xs)',
                    color: 'var(--sidebar-tertiary)',
                  }}>
                  {appointment.selectedArtists?.length
                    ? `Preferred: ${getSelectedArtistsText(
                        loadedUser,
                        appointment.selectedArtists,
                      )}`
                    : artistRoleLabel}
                </Typography>
              </Box>,
            ],
          },
    ].filter(Boolean) as SectionRow[],
  };

  // DO NOT CHANGE NAME without changing SideBarSection.tsx
  const activityEventCount = countActivityEvents(appointment);
  const activityHistorySection: Section = {
    name: 'Activity History',
    rows: [],
    values: { appointment, loadedUser },
    defaultExpanded: false,
    microLabel: true,
    summary: `${activityEventCount} event${
      activityEventCount === 1 ? '' : 's'
    }`,
  };

  // DO NOT CHANGE NAME without changing SideBarSection.tsx
  const payment: Section = {
    name: 'Payment',
    rows: [],
    values: {
      checkoutAction: openCheckoutModal,
      refreshModal,
      appointment,
      useSpecStyle: redesignActive,
    },
    ...(redesignActive ? { microLabel: true } : {}),
  };

  // DO NOT CHANGE NAME without changing SideBarSection.tsx
  const showTransfers =
    ((appointment.depositPrice ?? 0) > 0 &&
      (appointment.confirmedOn || appointment.depositPaidOn) &&
      isCardPaymentMethod(appointment.depositPaymentMethod)) ||
    ((appointment.price ?? 0) > 0 &&
      appointment.checkoutCompletedOn &&
      isCardPaymentMethod(appointment.paymentMethod));
  const transfers: Section | null = showTransfers
    ? {
        name: 'Transfer Details',
        rows: [],
        values: {
          appointment,
        },
        ...(redesignActive ? { defaultExpanded: false, microLabel: true } : {}),
      }
    : null;

  const transactions: Section = {
    name: 'Appointment Transactions',
    rows: [],
    values: {
      appointment,
      useSpecStyle: redesignActive,
    },
    ...(redesignActive ? { title: 'Transactions', microLabel: true } : {}),
  };

  const clientSection = getClientSection(appointment, {
    text: 'View',
    action: customerId => navigateToCustomer(customerId),
  });

  const client: Section = redesignActive
    ? {
        name: 'Client Card',
        title: 'Client',
        microLabel: true,
        rows: [],
        values: { appointment, onOpenChat, showChat, navigateToCustomer },
      }
    : clientSection;

  const tattooDetails: Section = {
    name: 'Tattoo Details',
    rows: [
      !!appointment.flashDesign &&
        (!!appointment.flashDesign.flashbook?.business ||
          !!appointment.employee) && {
          type: 'LINK',
          label: 'Flash Design',
          value: [
            {
              url: getAppointmentFlashDesignUrl(
                appointment.flashDesign,
                appointment.employee,
              ),
              label: appointment.flashDesign.name,
            },
          ],
        },
      !isNil(appointment.tattooServiceType) && {
        type: 'TEXT',
        label: 'Tattoo Service Type',
        value: [appointment.tattooServiceType || ''],
      },
      {
        type: 'TEXT',
        label: 'Internal Notes',
        value: [appointment.internalNotes || ''],
      },
      {
        type: 'TEXT',
        label: 'Tattoo Location',
        value: [
          getFullTattooLocation(
            appointment.tattooLocation,
            appointment.bodySide,
          ),
        ],
      },
      {
        type: 'TEXT',
        label: 'Tattoo Size',
        value: [size || ''],
      },
      {
        type: 'TEXT',
        label: 'Tattoo Color',
        value: [appointment.tattooColor || ''],
      },
      {
        type: 'TEXT',
        label: 'Tattoo Description',
        value: [appointment.tattooDetails || ''],
      },
      !isNil(appointment.otherNotes) && {
        type: 'TEXT',
        label: 'Other Notes',
        value: [appointment.otherNotes || ''],
      },
      !isNil(appointment.ongoingProject) && {
        type: 'TEXT',
        label: 'Ongoing Project',
        value: [getBooleanText(appointment.ongoingProject) || ''],
      },
      !isNil(appointment.coverUp) && {
        type: 'TEXT',
        label: 'Cover Up',
        value: [getBooleanText(appointment.coverUp) || ''],
      },
      !isNil(appointment.surroundingTattoos) && {
        type: 'TEXT',
        label: 'Surrounding Tattoos',
        value: [getBooleanText(appointment.surroundingTattoos) || ''],
      },
      !isNil(appointment.isEphemeral) && {
        type: 'TEXT',
        label: 'Is Ephemeral?',
        value: [getBooleanText(appointment.isEphemeral) || ''],
      },
    ].filter(Boolean) as SectionRow[],
  };

  const serviceDetails: Section = createServiceDetailsSection({
    appointment,
    service,
    piercingLocation,
  });

  const refImages = getAllRefImages(appointment);

  const photos: Section = {
    name: 'Photos',
    rows: [
      {
        type: 'IMAGE',
        label: 'Reference Images',
        value: refImages,
      },
      {
        type: 'IMAGE',
        label: 'Area Images',
        value: appointment.areaImgsId || [''],
      },
    ],
  };

  const referralSource = appointment.referralSources?.[0];

  const referralSourceSection = referralSource
    ? createReferralSourceSection(referralSource)
    : null;

  const detailSection = isTattoo ? tattooDetails : serviceDetails;

  const tattooLocationText = getFullTattooLocation(
    appointment.tattooLocation,
    appointment.bodySide,
  );

  const flashDesignField =
    !!appointment.flashDesign &&
    (!!appointment.flashDesign.flashbook?.business || !!appointment.employee)
      ? {
          label: 'Flash design',
          value: appointment.flashDesign.name,
          url: getAppointmentFlashDesignUrl(
            appointment.flashDesign,
            appointment.employee,
          ),
        }
      : null;

  const redesignGridFields = (
    [
      ...(isTattoo
        ? [
            { label: 'Placement', value: tattooLocationText },
            { label: 'Size', value: size },
            { label: 'Color', value: appointment.tattooColor },
            flashDesignField,
            { label: 'Service type', value: appointment.tattooServiceType },
            {
              label: 'Ongoing project',
              value: answerText(appointment.ongoingProject),
            },
            { label: 'Cover-up', value: answerText(appointment.coverUp) },
            {
              label: 'Surrounding tattoos',
              value: answerText(appointment.surroundingTattoos),
            },
            { label: 'Ephemeral', value: answerText(appointment.isEphemeral) },
          ]
        : [
            { label: 'Service', value: service?.serviceName },
            service?.serviceCategory === 'PIERCING' && {
              label: 'Piercing location',
              value: piercingLocation?.locationCategory,
            },
            service?.serviceCategory === 'PIERCING' && {
              label: 'Location details',
              value: piercingLocation?.locationType,
            },
          ]),
    ] as (
      | { label: string; value?: string | null; url?: string }
      | null
      | false
    )[]
  ).filter(
    (field): field is ServiceDetailGridField => !!field && !!field.value,
  );

  const redesignNoteFields = [
    { label: 'Internal note · internal', text: appointment.internalNotes },
    { label: 'Appointment note · internal', text: appointment.tattooDetails },
    { label: 'Other notes', text: appointment.otherNotes },
    !isTattoo && {
      label: 'General availability',
      text: appointment.availability,
    },
  ].filter((note): note is ServiceDetailNoteField => !!note && !!note.text);

  const redesignPhotoRows = [
    refImages.length > 0 && { label: 'Reference images', urls: refImages },
    !!appointment.areaImgsId?.length && {
      label: 'Area images',
      urls: appointment.areaImgsId,
    },
  ].filter((row): row is ServiceDetailPhotoRow => !!row);

  const mergedDetailSection: Section = redesignActive
    ? {
        name: 'Service Detail',
        title: `${categoryLabel ?? 'Service'} Details`,
        microLabel: true,
        rows: [],
        values: {
          gridFields: redesignGridFields,
          noteFields: redesignNoteFields,
          photoRows: redesignPhotoRows,
        },
      }
    : detailSection;

  // Spread first — row.value is a union of array types, which cannot be
  // filtered directly.
  const hasRows = (section: Section) =>
    section.rows.some(row => [...row.value].filter(Boolean).length > 0);

  const mergedDetailHasContent = redesignActive
    ? redesignGridFields.length > 0 ||
      redesignNoteFields.length > 0 ||
      redesignPhotoRows.length > 0
    : hasRows(mergedDetailSection);

  const sections = redesignActive
    ? ([
        overviewSection,
        client,
        mergedDetailHasContent ? mergedDetailSection : null,
        isConsultation ? null : payment,
        isConsultation || !hasTransactions ? null : transactions,
        isConsultation ? null : transfers,
        referralSourceSection && { ...referralSourceSection, microLabel: true },
        activityHistorySection,
      ].filter(Boolean) as Section[])
    : ([
        overview,
        isConsultation ? null : payment,
        isConsultation ? null : transactions,
        isConsultation ? null : transfers,
        client,
        hasRows(detailSection) ? detailSection : null,
        hasRows(photos) ? photos : null,
        referralSourceSection,
      ].filter(Boolean) as Section[]);

  const newModalData = {
    name: !isConsultation ? 'Appointment Detail' : 'Consultation Detail',
    sections: sections.filter(Boolean) as Section[],
  };

  return newModalData;
}
